import React, { useEffect, useRef, useState } from 'react';
import {createPortal} from 'react-dom';
import Lottie from 'react-lottie';
import finishAnimation from '../../lotties/finish-process-document.json';
import CardBaseProcessamentoSo from '../LogProcessamentoSo/CardBaseProcessamentoSo';

const POLL_INTERVAL = 1000;
const FINISH_TRANSITION_MS = 4500;
const ERROR_MESSAGES = {
  document_download_failed: 'Falha ao baixar um documento autorizado.',
  document_conversion_failed: 'Falha ao converter um documento editável.',
  invalid_document_pdf: 'Um dos documentos não gerou um PDF válido.',
  pdf_merge_failed: 'Falha ao unificar os documentos do processo.',
  invalid_materialization_pdf: 'O arquivo final gerado não é um PDF válido.',
  render_failed: 'Falha durante a montagem do processo.',
  build_401: 'A sessão de montagem não é válida.',
  build_403: 'Você não possui autorização para esta montagem.',
  build_404: 'O processo ou contexto de montagem não foi encontrado.',
  build_422: 'O contexto ou manifesto expirou ou deixou de ser válido.',
  status_401: 'A sessão de montagem não é válida.',
  status_403: 'Você não possui autorização para acompanhar esta montagem.',
  status_404: 'O acompanhamento da montagem não foi encontrado.',
  status_422: 'O contexto ou manifesto expirou ou deixou de ser válido.'
};

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const StageList = ({stages = []}) => (
  <ul className='list-group list-group-flush'>
    {stages.map(stage => {
      const content = {
        processing: <><i className='fas fa-cog fa-spin fa-lg' /><strong className='ml-2'>{stage.label}</strong></>,
        waiting: <span className='text-muted opacity-50'><i className='fas fa-stopwatch fa-lg' /><span className='ml-2'>{stage.label}</span></span>,
        complete: <><i className='fas fa-check fa-lg text-success' /><span className='ml-2'>{stage.label}</span></>,
        error: <span className='text-danger'><i className='fas fa-times fa-lg' /><span className='ml-2'>{stage.label}</span></span>
      }[stage.status];
      return <li className='list-group-item' key={stage.id}>{content}</li>;
    })}
  </ul>
);

const MaterializacaoV2Build = ({numeroProcesso, buildPath, statusPath, pdfViewerPath, pdfDownloadPath}) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [viewMode, setViewMode] = useState('folder');
  const pollRef = useRef();
  const finishTimerRef = useRef();

  const loadStatus = async () => {
    const response = await fetch(statusPath, {credentials: 'same-origin', headers: {'Accept': 'application/json'}});
    if (!response.ok) throw new Error(`status_${response.status}`);
    const data = await response.json();
    setStatus(data);
    if (data.state === 'complete' || data.state === 'error') clearInterval(pollRef.current);
  };

  const start = async (rebuild = false) => {
    setOpen(true);
    setViewMode('build');
    setStatus(null);
    setRequestError(null);
    try {
      const requestPath = rebuild ? `${buildPath}${buildPath.includes('?') ? '&' : '?'}rebuild=true` : buildPath;
      const response = await fetch(requestPath, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Accept': 'application/json', 'X-CSRF-Token': csrfToken()}
      });
      if (!response.ok) throw new Error(`build_${response.status}`);
      setStatus(await response.json());
      pollRef.current = setInterval(() => loadStatus().catch(error => {
        clearInterval(pollRef.current);
        setRequestError(error.message);
      }), POLL_INTERVAL);
      await loadStatus();
    } catch (error) {
      setRequestError(error.message);
    }
  };

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearTimeout(finishTimerRef.current);
  }, []);

  useEffect(() => {
    const folderContainer = document.getElementById('materializacao-v2-folder');
    const pdfContainer = document.getElementById('materializacao-v2-pdf');
    const showFolder = !open || viewMode === 'folder';
    folderContainer?.classList.toggle('d-none', !showFolder);
    pdfContainer?.classList.toggle('d-none', showFolder);

    return () => {
      folderContainer?.classList.remove('d-none');
      pdfContainer?.classList.add('d-none');
    };
  }, [open, viewMode]);

  useEffect(() => {
    if (status?.state !== 'complete') return undefined;

    setViewMode('finish');
    finishTimerRef.current = setTimeout(() => setViewMode('pdf'), FINISH_TRANSITION_MS);
    return () => clearTimeout(finishTimerRef.current);
  }, [status?.state]);

  const toggleFullscreen = () => {
    const content = document.getElementById('materializacao-v2-content');
    if (document.fullscreenElement) document.exitFullscreen();
    else content?.requestFullscreen();
  };

  const confirmRefresh = async () => {
    const message = `
      <div style="text-align: left;">
        Sistema informa:<br/>
        Você está prestes a iniciar um novo processo de montagem, isto pode demorar, pois:<br/><br/>
        1 - Virão a última versão dos documentos anexados;<br/>
        2 - Virão a última versão dos documentos substituidos;<br/>
        3 - Virão documentos com a versão contendo todas as assinaturas já realizadas;<br/>
        4 - Ocultará todos os documentos cancelados.<br/>
      </div>`;
    const result = await window.Swal.fire({
      title: 'Você tem certeza disso?',
      html: message,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
      showCancelButton: true,
      confirmButtonColor: '#199B91'
    });
    if (result?.isConfirmed) await start(true);
  };

  const returnToFolder = () => {
    clearInterval(pollRef.current);
    clearTimeout(finishTimerRef.current);
    setOpen(false);
    setStatus(null);
    setRequestError(null);
    setViewMode('folder');
    window.history.replaceState({}, '', window.location.pathname);
  };

  if (!open) {
    return (
      <button className='btn btn-lg btn-outline-light mx-1' title='Montar Processo' data-bs-toggle='tooltip' data-bs-placement='bottom' onClick={() => start()}>
        <i className='fas fa-file-export' />
      </button>
    );
  }

  const pdfContainer = document.getElementById('materializacao-v2-pdf');
  if (!pdfContainer) return null;

  if (status?.state === 'complete' && viewMode === 'finish') {
    return createPortal(
      <div className='bg-white overflow-auto h-100'>
        <CardBaseProcessamentoSo numeroProcesso={numeroProcesso} tag='new_folder_processo_so' finished={true}>
          <div className='card-body text-center'>
            <Lottie
              options={{loop: false, autoplay: true, animationData: finishAnimation}}
              height={300}
              width={350}
            />
          </div>
        </CardBaseProcessamentoSo>
      </div>,
      pdfContainer
    );
  }

  if (status?.state === 'complete' && viewMode === 'pdf') {
    const controls = (
      <>
        <button className='btn btn-lg btn-outline-light mx-1' title='Tela Cheia' onClick={toggleFullscreen}>
          <i className='fas fa-expand-arrows-alt' />
        </button>
        <a className='btn btn-lg btn-outline-light mx-1' title='Baixar Processo' href={pdfDownloadPath} target='_blank' rel='nofollow'>
          <i className='fas fa-file-download' />
        </a>
        <button className='btn btn-lg btn-outline-light mx-1' title='Ver Processo no Navegador' onClick={returnToFolder}>
          <i className='fas fa-sitemap' />
        </button>
        <button className='btn btn-lg btn-outline-light mx-1' title='Atualizar Visualização do Processo' onClick={confirmRefresh}>
          <i className='fas fa-sync-alt' />
        </button>
      </>
    );
    const viewer = createPortal(
      <iframe id='pdf_view' frameBorder='0' src={pdfViewerPath} title='webviewer' height='100%' width='100%' scrolling='no' />,
      pdfContainer
    );
    return <>{controls}{viewer}</>;
  }

  return createPortal(
    <div className='bg-white overflow-auto h-100'>
      <CardBaseProcessamentoSo numeroProcesso={numeroProcesso} tag='new_folder_processo_so' finished={false}>
        {requestError || status?.state === 'error' ? (
          <div className='card-body text-center text-danger'>
            <div className='fa-8x'><i className='fas fa-exclamation-triangle' /></div>
            <div>Não foi possível montar o processo.</div>
            <small>{ERROR_MESSAGES[status?.error || requestError] || 'A montagem foi encerrada com segurança.'}</small>
          </div>
        ) : status ? <StageList stages={status.stages} /> : (
          <div className='card-body text-center fa-10x text-primary'><i className='fas fa-cog fa-spin' /></div>
        )}
      </CardBaseProcessamentoSo>
    </div>,
    pdfContainer
  );
};

export default MaterializacaoV2Build;
